import { PageHeaderWithBackButton } from '../../components/common';
import {
  DatePicker,
  Editor,
  Form,
  ImageUpload,
  InputText,
  InputNumber,
  Select,
  SubmitButton,
  TextArea,
} from '../../components/common/form';
import * as Yup from 'yup';
import { Col, Row, Spin } from 'antd';
import {
  CreateFavePassForm,
  FavePass,
  useCreateFavePass,
  useFetchBankAccounts,
  useFetchEnums,
  useFetchFavePassDetails,
  useFetchMainCategories,
  useUpdateFavePass,
} from '../../services/FavePass';
import { Alert, Message, RcFile } from 'fave-ui';
import { useNavigate, useParams } from 'react-router';
import { FormikHelpers } from 'formik';
import { addDays, isBefore, startOfDay } from 'date-fns';
import {
  convertToDays,
  formatDate,
  getValidity,
} from '../../utils/utilFunctions';
import { useUserContext } from '../../contexts/UserContext';
import { validityTypes } from '../../helpers/defaults';

const FavePassView = () => {
  const { id, favePassId } = useParams();
  const parsedCompanyId = String(id);
  // can be either create, edit mode
  const parsedFavePassId = String(favePassId);
  const hasFavePassId = !!favePassId;
  // to check if params are valid
  const isValidParams = parsedCompanyId !== '0' && parsedFavePassId !== '0';
  const navigate = useNavigate();
  // fetch user settings for fave pass
  const { getPageSettings } = useUserContext();
  const partnerSettings = getPageSettings('partners');
  const canShowFavePass = !!partnerSettings?.show_fave_passes;
  const canUpdateFavePass = !!partnerSettings?.update_fave_passes;
  const disableFormFields = !canUpdateFavePass;
  // if have fave pass id, then it is edit mode, else create mode
  const isEditMode = !!hasFavePassId;
  const pageTitle = `${isEditMode ? 'Edit' : 'Create'} Fave Pass`;

  // get fave pass data if in edit mode
  const { data: favePassDetailsData, isFetching: isFetchingFavePassDetails } =
    useFetchFavePassDetails({ id: parsedFavePassId }, isEditMode);
  // use createFavePass for create new fave pass
  const { mutate: createFavePass, isLoading: isLoadingCreateFavePass } =
    useCreateFavePass();
  // use updateFavePass for updating existing fave pass
  const { mutate: updateFavePass, isLoading: isLoadingUpdateFavePass } =
    useUpdateFavePass();
  // fetch enums for status and reservation requirement type select items
  const { data: favePassEnums, isFetching: isFetchingEnums } = useFetchEnums();
  // fetch main categories for main category select items
  const { data: favePassMainCategories, isFetching: isFetchingMainCategories } =
    useFetchMainCategories();
  // fetch bank accounts for dropdown
  const { data: bankAccounts, isFetching: isFetchingCompanyBankAccounts } =
    useFetchBankAccounts({
      companyId: Number(parsedCompanyId),
    });

  // wait until all necessary data is fetched
  const isFormLoading =
    isFetchingFavePassDetails ||
    isLoadingCreateFavePass ||
    isLoadingUpdateFavePass ||
    isFetchingEnums ||
    isFetchingMainCategories ||
    isFetchingCompanyBankAccounts;

  // form fields for fave pass
  const formFields: FormFieldsRecord = {
    name: { label: 'Name', type: 'input', key: 'name', col: 24 },
    description: {
      label: 'Description',
      type: 'input',
      col: 24,
      key: 'description',
    },
    total_sessions: {
      label: 'Total Sessions',
      type: 'number',
      key: 'total_sessions',
      col: 12,
    },
    fee_percentage: {
      label: 'Fave Fee %',
      type: 'number',
      key: 'fee_percentage',
      col: 12,
    },
    validity: { label: 'Validity', type: 'number', key: 'validity', col: 12 },
    validity_type: {
      label: 'Validity Type',
      type: 'select',
      key: 'validity_type',
      col: 12,
      options: validityTypes,
    },
    max_cap: { label: 'Max Cap', type: 'number', key: 'max_cap', col: 12 },
    user_purchase_limit: {
      label: 'User Purchase Limit ( if empty, no limit )',
      type: 'number',
      key: 'user_purchase_limit',
      col: 12,
    },
    main_category_id: {
      label: 'Main Category',
      type: 'select',
      key: 'main_category_id',
      options:
        favePassMainCategories?.main_categories?.map(
          ({ id, category_type }) => ({
            key: id,
            label: category_type,
            // send id as value to API
            value: id,
          }),
        ) || [],
      col: 24,
    },
    original_amount: {
      label: 'Original Amount',
      type: 'number',
      key: 'original_amount',
      col: 12,
    },
    discounted_amount: {
      label: 'Discounted Amount',
      type: 'number',
      key: 'discounted_amount',
      col: 12,
    },
    grabpay_later_fee_percentage: {
      label: 'Grabpay Later %',
      type: 'number',
      key: 'grabpay_later_fee_percentage',
      col: 12,
    },
    finance_account_id: {
      label: 'Finance Account',
      type: 'select',
      key: 'finance_account_id',
      options:
        bankAccounts?.finance_accounts?.map(({ id, bank_account_name }) => ({
          key: id,
          label: bank_account_name,
          // send id as value to API
          value: id,
        })) || [],
      col: 12,
    },
    start_date: {
      label: 'Start Date',
      type: 'datepicker',
      key: 'start_date',
      col: 12,
    },
    end_date: {
      label: 'End Date',
      type: 'datepicker',
      key: 'end_date',
      col: 12,
    },
    status: {
      label: 'Status',
      type: 'select',
      key: 'status',
      options:
        favePassEnums?.status.map(({ key, label }) => ({
          key,
          label,
          // send key as value to API
          value: key,
        })) || [],
      col: 12,
    },
    reservation_requirement_type: {
      label: 'Reservation Requirement Type',
      type: 'select',
      key: 'reservation_requirement_type',
      options:
        favePassEnums?.reservation_requirement_type.map(({ key, label }) => ({
          key,
          label,
          // send key as value to API
          value: key,
        })) || [],
      col: 12,
    },
    what_you_get: {
      label: 'What You Get',
      type: 'editor',
      key: 'what_you_get',
    },
    fine_print: { label: 'Fine Print', type: 'editor', key: 'fine_print' },
    cancellation_policy: {
      label:
        'Cancellation Policy ( if empty, we will follow default cancellation policy. )',
      type: 'textarea',
      key: 'cancellation_policy',
    },

    image: { label: 'Image (327px x 176px)', type: 'image', key: 'image_url' },
  };

  // form schema for form validation
  const formSchema = Yup.object().shape({
    [formFields.name.key]: Yup.string()
      .max(120, 'Character limit is 120')
      .required('Required'),
    [formFields.description.key]: Yup.string()
      .max(120, 'Character limit is 120')
      .required('Required'),
    [formFields.total_sessions.key]: Yup.number()
      .moreThan(2, 'Minimum sessions is 3')
      .typeError('Required')
      .required('Required'),
    [formFields.original_amount.key]: Yup.number()
      .min(0.01, 'Minimum is 0.01')
      .typeError('Required')
      .required('Required')
      .min(0, 'Minimum is 0'),
    [formFields.discounted_amount.key]: Yup.number()
      .min(0.01, 'Minimum is 0.01')
      .typeError('Required')
      .required('Required')
      .when(
        formFields.original_amount.key,
        (originalAmount: number, schema: Yup.NumberSchema) => {
          return schema.test(
            'is-less-than-original',
            'Discounted amount must be strictly less than the original amount',
            discountedAmount => {
              return discountedAmount !== undefined &&
                originalAmount !== undefined
                ? discountedAmount < originalAmount
                : true;
            },
          );
        },
      ),
    [formFields.fee_percentage.key]: Yup.number()
      .min(0, 'Minimum is 0')
      .max(100, 'Maximum is 100')
      .typeError('Required')
      .required('Required'),
    [formFields.grabpay_later_fee_percentage.key]: Yup.number()
      .min(0, 'Minimum is 0')
      .max(100, 'Maximum is 100')
      .nullable(),
    // in edit more, if prefilled start date is in the past
    // user should still be able to submit the form
    [formFields.start_date.key]: Yup.date()
      .test(
        'is-valid-date',
        'Start date cannot be in the past',
        (value, context) => {
          if (!(value instanceof Date) || isNaN(value.getTime())) {
            return false;
          }

          const today = startOfDay(new Date());
          // Check if value is prefilled
          const isPrefilled = !!context.parent[formFields.start_date.key];

          // Allow the prefilled date if it's in the past
          if (isPrefilled && isBefore(value, today)) {
            return true;
          }

          return value >= today;
        },
      )
      .typeError('Please select a start date')
      .required('Required'),
    [formFields.end_date.key]: Yup.date()
      .typeError('Please select an end date')
      .required('End date is required')
      .when(
        formFields.start_date.key,
        (startDate: Date, schema: Yup.DateSchema) => {
          if (startDate) {
            return schema.min(
              addDays(startOfDay(startDate), 1),
              'End date must be strictly after the start date',
            );
          }
          return schema;
        },
      ),
    [formFields.status.key]: Yup.string().required('Required'),
    [formFields.reservation_requirement_type.key]:
      Yup.string().required('Required'),
    [formFields.max_cap.key]: Yup.number().min(0, 'Minimum is 0').nullable(),
    [formFields.user_purchase_limit.key]: Yup.number()
      .min(0, 'Minimum is 0')
      .nullable(),
    [formFields.validity.key]: Yup.number()
      .min(0, 'Minimum is 0')
      .typeError('Required')
      .required('Required'),
    [formFields.validity_type.key]: Yup.string().required('Required'),
    [formFields.what_you_get.key]: Yup.string().required('Required'),
    [formFields.fine_print.key]: Yup.string().required('Required'),
    [formFields.cancellation_policy.key]: Yup.string().nullable(),
    [formFields.main_category_id.key]: Yup.string().required('Required'),
    [formFields.finance_account_id.key]: Yup.string().required('Required'),
    // no need to add validation for jpg, jpeg, png as it is handled by ImageUpload component
    [formFields.image.key]: Yup.array().of(
      Yup.mixed().test(
        'fileSize',
        'The file size is too large. Maximum allowed size is 10MB.',
        value => {
          const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

          // in edit mode, value.size is not available
          if (!value.size) return true;
          // Ensure a file is provided and check the size
          return value.size <= MAX_FILE_SIZE;
        },
      ),
    ),
  });

  const { days: validityDays, type: validityType } = getValidity(
    favePassDetailsData?.validity,
  );

  // form initial values
  const initialValues = {
    [formFields.name.key]: favePassDetailsData?.name,
    [formFields.description.key]: favePassDetailsData?.description,
    [formFields.total_sessions.key]: favePassDetailsData?.total_sessions,
    [formFields.original_amount.key]:
      favePassDetailsData?.original_amount.amount,
    [formFields.discounted_amount.key]:
      favePassDetailsData?.discounted_amount.amount,
    [formFields.fee_percentage.key]: favePassDetailsData?.fee_percentage,
    [formFields.grabpay_later_fee_percentage.key]:
      favePassDetailsData?.grabpay_later_fee_percentage,
    [formFields.start_date.key]: favePassDetailsData?.start_date,
    [formFields.end_date.key]: favePassDetailsData?.end_date,
    [formFields.status.key]: favePassDetailsData?.status,
    [formFields.reservation_requirement_type.key]:
      favePassDetailsData?.reservation_requirement_type,
    [formFields.max_cap.key]: favePassDetailsData?.max_cap,
    [formFields.user_purchase_limit.key]:
      favePassDetailsData?.user_purchase_limit,
    [formFields.validity.key]: validityDays,
    [formFields.validity_type.key]: validityType,
    [formFields.what_you_get.key]: favePassDetailsData?.what_you_get,
    [formFields.fine_print.key]: favePassDetailsData?.fine_print,
    [formFields.cancellation_policy.key]:
      favePassDetailsData?.cancellation_policy,
    [formFields.main_category_id.key]: favePassDetailsData?.main_category_id,
    [formFields.finance_account_id.key]:
      favePassDetailsData?.finance_account_id,
    [formFields.image.key]: favePassDetailsData?.image_url
      ? // so that ImageUpload can pickup value via FormikContext
        [
          {
            url: favePassDetailsData.image_url,
            thumbUrl: favePassDetailsData.image_url,
            uid: 'default-uid',
            name: 'default-file',
          },
        ]
      : [],
  };

  // form input fields
  const FormFields = () => (
    <>
      {Object.values(formFields).map(key => {
        if (key.type === 'select') {
          return (
            <Col key={key.key} span={key.col}>
              <Select
                name={key.key}
                label={key.label}
                placeholder=""
                bordered
                options={key.options}
                allowClear={false}
                disabled={disableFormFields}
              />
            </Col>
          );
        } else if (key.type === 'datepicker') {
          return (
            <Col span={key.col} key={key.key}>
              <DatePicker
                key={key.key}
                name={key.key}
                label={key.label}
                disabledDate={currentDate =>
                  // cannot select past dates
                  currentDate < startOfDay(new Date())
                }
                allowClear={false}
                disabled={disableFormFields}
              />
            </Col>
          );
        } else if (key.type === 'editor') {
          return (
            <Col span={24} key={key.key}>
              <Editor
                name={key.key}
                label={key.label}
                disabled={disableFormFields}
              />
            </Col>
          );
        } else if (key.type === 'image') {
          return (
            <Col span={24} key={key.key}>
              <ImageUpload
                name={key.key}
                label={key.label}
                accept={'.jpg, .jpeg, .png'}
                disabled={disableFormFields}
              />
            </Col>
          );
        } else if (key.type === 'number') {
          return (
            <Col span={key.col} key={key.key}>
              <InputNumber
                name={key.key}
                label={key.label}
                bordered
                disabled={disableFormFields}
              />
            </Col>
          );
        } else if (key.type === 'textarea') {
          return (
            <Col span={24} key={key.key}>
              <TextArea
                name={key.key}
                label={key.label}
                disabled={disableFormFields}
                rows={5}
              />
            </Col>
          );
        } else {
          return (
            <Col span={key.col} key={key.key}>
              <InputText
                name={key.key}
                label={key.label}
                placeholder=""
                allowClear
                bordered
                disabled={disableFormFields}
              />
            </Col>
          );
        }
      })}
    </>
  );

  // form submit handler
  const onFormSubmit = (
    {
      image_url,
      start_date,
      end_date,
      validity,
      validity_type,
      ...restFormValues
    }: CreateFavePassForm,
    { setSubmitting }: FormikHelpers<CreateFavePassForm>,
  ) => {
    const imageObject = (
      image_url && image_url.length > 0 ? image_url[0].originFileObj : undefined
    ) as RcFile;

    const handleSuccess = (message: string) => {
      Message.success({ content: message });
      // redirect to fave pass listing page on success fave pass creation / update
      navigate(`/admin/partners/${parsedCompanyId}/fave_passes`);
    };

    // calculate validity in days based on selected validity type
    const validityInDays = convertToDays(validity, validity_type);

    if (isEditMode) {
      const payload: Omit<FavePass, 'company_id' | 'validity_type'> = {
        ...restFormValues,
        validity: validityInDays,
        ...(imageObject && { image: imageObject }),
        start_date: formatDate(start_date, 'yyyy-MM-dd'),
        end_date: formatDate(end_date, 'yyyy-MM-dd'),
      };

      const formData = new FormData();

      for (const key in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, key)) {
          const value = payload[key as keyof typeof payload];
          if (value instanceof File) {
            // Append files directly
            formData.append(`fave_pass[${key}]`, value);
          } else {
            // Append other values as strings
            formData.append(
              `fave_pass[${key}]`,
              value != null ? String(value) : '',
            );
          }
        }
      }

      updateFavePass(
        { fave_pass: formData, fave_pass_id: parsedFavePassId },
        {
          onSuccess: () => handleSuccess('Successfully updated Fave Pass'),
          onSettled: () => {
            setSubmitting(false);
          },
        },
      );
    } else {
      const payload: Omit<FavePass, 'validity_type'> = {
        ...restFormValues,
        validity: validityInDays,
        company_id: parsedCompanyId,
        ...(imageObject && { image: imageObject }),
        start_date: formatDate(start_date, 'yyyy-MM-dd'),
        end_date: formatDate(end_date, 'yyyy-MM-dd'),
      };

      const formData = new FormData();

      for (const key in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, key)) {
          const value = payload[key as keyof typeof payload];
          if (value instanceof File) {
            // Append files directly
            formData.append(`fave_pass[${key}]`, value);
          } else {
            // Append other values as strings
            formData.append(
              `fave_pass[${key}]`,
              value != null ? String(value) : '',
            );
          }
        }
      }

      createFavePass(
        { fave_pass: formData },
        {
          onSuccess: () => handleSuccess('Successfully created Fave Pass'),
          onSettled: () => {
            setSubmitting(false);
          },
        },
      );
    }
  };

  return (
    <>
      <PageHeaderWithBackButton title={pageTitle} />
      <div className={'style.formBackground'}>
        {/* form page based on user permissions and valid params */}
        {canShowFavePass ? (
          isValidParams ? (
            <Spin spinning={isFormLoading}>
              <Form
                initialValues={initialValues}
                onSubmit={onFormSubmit}
                validationSchema={formSchema}
                validateOnMount
                validateOnChange
                validateOnSchemaChange
              >
                <div className="flex justify-center">
                  <div className="max-w-[750px]">
                    <Row gutter={24}>
                      <FormFields />
                    </Row>
                    <div className={'flex justify-end'}>
                      <SubmitButton
                        disableIfNotValid
                        isDisabled={disableFormFields}
                        className={'style.submitButton'}
                      >
                        {(_, isSubmitting) =>
                          isSubmitting ? 'Saving...' : 'Submit'
                        }
                      </SubmitButton>
                    </div>
                  </div>
                </div>
              </Form>
            </Spin>
          ) : (
            <Alert
              showIcon
              type={'error'}
              message={'Invalid params found in URL'}
            />
          )
        ) : (
          <Alert
            showIcon
            type={'error'}
            message={'You do not have permission to view this page'}
          />
        )}
      </div>
    </>
  );
};

export default FavePassView;

// types for fave pass form fields
type FormField =
  | InputField
  | NumberField
  | TextareaField
  | DatepickerField
  | SelectField
  | ImageField
  | EditorField;

type FormFieldsRecord = Record<string, FormField>;

type InputField = {
  label: string;
  type: 'input';
  key: string;
  col?: number;
};

type NumberField = {
  label: string;
  type: 'number';
  key: string;
  col?: number;
};

type TextareaField = {
  label: string;
  type: 'textarea';
  key: string;
};

type EditorField = {
  label: string;
  type: 'editor';
  key: string;
};

type DatepickerField = {
  label: string;
  type: 'datepicker';
  key: string;
  col?: number;
};

type SelectOption = {
  label: string;
  key: string;
  value: string;
};

type SelectField = {
  label: string;
  type: 'select';
  key: string;
  options: SelectOption[];
  col?: number;
};

type ImageField = {
  label: string;
  type: 'image';
  key: string;
};
